import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Ratings from 'react-ratings-declarative'
import { Chevron, MonetizationButton, RecommendedCheckMark, RecommendedIcon } from '@jelly/components'
import { useTheme } from 'emotion-theming'
import { makeImgResizerUrl } from '@jelly/utils'
import { generateMarketingIdUrl } from '.'
import { useLocation } from '@reach/router'

const Wrapper = styled.div`
	border: ${({ isFirstCard, theme }) => (isFirstCard ? `5px solid #12A1BA` : `1px solid ${theme.colors.gray[200]}`)};
	margin: 0 auto 3rem;
	max-width: 400px;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		max-width: none;
	}
`
const CardTitleWrapper = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	align-items: center;
	background-color: ${({ isFirstCard, theme }) => (isFirstCard ? `#12A1BA` : theme.colors.gray[200])};
	color: ${({ isFirstCard, theme }) => (isFirstCard ? theme.colors.white : theme.colors.primary)};
	display: flex;
	height: 4rem;
	justify-content: center;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		justify-content: space-between;
		padding: 0 1rem;
	}
`
const CardTitle = styled.span`
	font-size: 20px;
	letter-spacing: -0.31px;
	line-height: 30px;
	text-align: center;

	${({ theme }) => theme.MQ.M} {
		font-size: 28px;
		letter-spacing: -0.43px;
		line-height: 42px;
		text-align: left;
	}
`
const MainContent = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	height: auto;
	justify-content: center;
	min-height: 240px;
	padding: 2rem 1rem;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
		flex-flow: row wrap;
		padding: 1rem 2rem;
		justify-content: space-between;
		> * {
			flex: 0 1 calc(33% - 1rem);
		}
	}
`
const MobileRecommendedWrapper = styled.div`
	max-width: 225px;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		display: none;
	}
`
const DesktopRecommendedWrapper = styled.div`
	display: none;

	${({ theme }) => theme.MQ.M} {
		${({ theme }) => theme.fonts.primaryMedium};
		color: ${({ theme }) => theme.colors.white};
		display: flex;
	}
`
const RecommendedText = styled.span`
	font-size: 1.25rem;
	letter-spacing: -0.31px;
	line-height: 30px;
	margin-right: 5px;
`
const ServiceDetails = styled.div`
	display: flex;
	flex-direction: column;
`
const ServiceImage = styled.img`
	margin: 2rem auto;
	height: auto;
	max-height: 100px;
	max-width: 225px;
	width: auto;

	${({ theme }) => theme.MQ.M} {
		margin: 0 auto 1rem 0;
	}
`
const RatingText = styled.div`
	${({ theme }) => theme.fonts.primaryMedium};
	color: ${({ theme }) => theme.colors.gray[500]};
	font-size: 0.875rem;
	letter-spacing: -0.22px;
	text-align: center;

	${({ theme }) => theme.MQ.M} {
		text-align: left;
	}
`
const RatingWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;

	${({ theme }) => theme.MQ.M} {
		justify-content: flex-start;
	}
`
const RatingNumber = styled.span`
	${({ theme }) => theme.fonts.primarySemiBold};
	color: ${({ theme }) => theme.colors.tertiary};
	font-size: 1.625rem;
	letter-spacing: -0.4px;
	line-height: 39px;
	margin-right: 0.5rem;
`
const BulletsWrapper = styled.div`
	border-left: 1px solid ${({ theme }) => theme.colors.gray[200]};
	border-right: 1px solid ${({ theme }) => theme.colors.gray[200]};
	display: none;

	${({ theme }) => theme.MQ.M} {
		display: block;
	}
`
const Bullet = styled.li`
	color: ${({ theme }) => theme.colors.gray[500]};
	margin-bottom: 0.5rem;
	padding: 0 0.5rem;
	text-align: left;
	word-break: break-word;

	&::marker {
		color: ${({ theme }) => theme.colors.tertiary};
		font-size: 20px;
	}
`
const ButtonsContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	margin: 10px auto;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		margin: 0;
	}
`
const FlexRow = styled.div`
	align-items: center;
	display: flex;
`
const ViewMoreDetailsContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	margin: 1rem auto;

	${({ theme }) => theme.MQ.M} {
		display: none;
	}
`
const ViewMoreText = styled.p`
	${({ theme }) => theme.fonts.primaryMedium};
	color: ${({ theme }) => theme.colors.tertiary};
	cursor: pointer;
	font-size: 0.875rem;
	letter-spacing: 1.12px;
	line-height: 21px;
	margin-right: 8px;
	text-align: center;
	text-transform: uppercase;
`

/**
 * Detailed Card that consists of a card title, rating, bullets, and offer buttons
 */
export function DetailedCard({ service }) {
	const { id, name, image, cardTitle, url, bullets, rating, rfqCatId } = service
	const isFirstCard = id === '1'
	const [showDetails, setShowDetails] = useState(false)
	const [marketingIdUrl, setMarketingIdUrl] = useState(url)
	const { search } = useLocation()

	useEffect(() => {
		// Set the first card to be open, showing the bullets
		setShowDetails(isFirstCard ? true : false)

		// replace marketingId with the query marketingId value
		setMarketingIdUrl(generateMarketingIdUrl(search, url))
	}, [isFirstCard, search, url])

	const {
		colors: { secondary, tertiary },
	} = useTheme()
	return (
		<Wrapper isFirstCard={isFirstCard}>
			<CardTitleWrapper isFirstCard={isFirstCard}>
				<CardTitle>{cardTitle}</CardTitle>
				{isFirstCard && (
					<DesktopRecommendedWrapper>
						<RecommendedText>Recommended</RecommendedText>
						<span>
							<RecommendedCheckMark />
						</span>
					</DesktopRecommendedWrapper>
				)}
			</CardTitleWrapper>

			<MainContent>
				{isFirstCard && (
					<MobileRecommendedWrapper>
						<RecommendedIcon />
					</MobileRecommendedWrapper>
				)}
				<ServiceDetails>
					<ServiceImage
						src={makeImgResizerUrl({
							src: image,
							method: 'h',
							h: '225-png',
						})}
						alt={name}
					/>
					<RatingText>Overall Rating:</RatingText>
					<RatingWrapper>
						<RatingNumber>{rating}</RatingNumber>
						<Ratings
							rating={Number(rating)}
							widgetDimensions="25px"
							widgetRatedColors={secondary}
							widgetSpacings="1px"
						>
							<Ratings.Widget />
							<Ratings.Widget />
							<Ratings.Widget />
							<Ratings.Widget />
							<Ratings.Widget />
						</Ratings>
					</RatingWrapper>
				</ServiceDetails>
				<BulletsWrapper>
					<Bullets bullets={bullets} />
				</BulletsWrapper>
				<ButtonsContainer>
					<MonetizationButton
						pricing={{
							affiliate: marketingIdUrl
								? {
										cta: 'Visit Site',
										link: marketingIdUrl,
										name: 'QT Program',
										isAddQtParams: false,
								  }
								: undefined,
							isFeatured: false,
							primary: true,
							selected: 'affiliate',
							widget: {
								cta: 'Compare Quotes',
								id: rfqCatId,
								isExclusive: false,
							},
							widgetId: rfqCatId,
						}}
						rank={id}
						sectionType="detailedCard"
						serviceName={name}
						templateType="top5landingpage"
						type="top5card"
					/>
				</ButtonsContainer>
				<ViewMoreDetailsContainer onClick={() => setShowDetails(!showDetails)}>
					<FlexRow>
						<ViewMoreText>View {!showDetails ? 'More' : 'Less'} Details</ViewMoreText>
						<Chevron color={tertiary} height={12} width={12} rotationDeg={!showDetails ? 180 : 0} />
					</FlexRow>
					{showDetails && <Bullets bullets={bullets} />}
				</ViewMoreDetailsContainer>
			</MainContent>
		</Wrapper>
	)
}

const Bullets = ({ bullets }) => {
	return (
		<ul>
			{bullets.map((bullet, index) => (
				<Bullet key={index}>{bullet}</Bullet>
			))}
		</ul>
	)
}
